import {
  Button,
  Separator,
  Skeleton,
  Text,
  useAlert,
} from "@prismicio/editor-ui"
import { Suspense, useCallback, useState } from "react"

import {
  LocaleSelection,
  type SelectedLocale,
} from "../app/components/locales/LocaleSelection"
import { onboardingWithImages } from "../app/constants"
import type { RepositoryLocale } from "../app/services/locales"
import { useSharedOnboarding } from "../app/services/sharedOnboarding"
import { trackPbOnboardingStep } from "../app/services/tracking"
import { getMasterLocale } from "../settings/locales/localeUtils"
import { useDefaultLocalesQuery } from "../settings/locales/useDefaultLocales"
import {
  useAddRepositoryLocaleMutation,
  useDeleteRepositoryLocaleMutation,
  useRepositoryLocalesQuery,
} from "../settings/locales/useRepositoryLocales"
import { Step, useOnboarding } from "./OnboardingSteps"

const defaultMasterLocaleId = "en-us"
const onboardingStepName = "chooseMainLanguage"

type ChooseMainLocaleStepProps = {
  shouldAnimate: boolean
}

export function ChooseMainLocaleStep(props: ChooseMainLocaleStepProps) {
  const { shouldAnimate } = props

  return (
    <Step
      image={onboardingWithImages.chooseLanguage}
      shouldAnimate={shouldAnimate}
    >
      <Text variant="h3" sx={{ marginBottom: 8 }}>
        What's your main content language?
      </Text>

      <Text variant="normal" color="grey11" sx={{ marginBottom: 24 }}>
        Choose the language for creating content. More languages can be added
        later in settings.
      </Text>

      <Suspense fallback={<ChooseMainLocaleContentSkeleton />}>
        <ChooseMainLocaleContent />
      </Suspense>
    </Step>
  )
}

export function ChooseMainLocaleContent() {
  const { goToNextStep } = useOnboarding()
  const sharedOnboarding = useSharedOnboarding()
  const { showAlert } = useAlert()

  const { data: defaultLocales } = useDefaultLocalesQuery()
  const { data: repositoryLocales } = useRepositoryLocalesQuery()
  const addRepositoryLocaleMutation = useAddRepositoryLocaleMutation()
  const deleteRepositoryLocaleMutation = useDeleteRepositoryLocaleMutation()
  const previousMasterLocale = getMasterLocale(repositoryLocales)

  const [selectedMasterLocale, setSelectedMasterLocale] =
    useState<SelectedLocale>(
      getSelectedMasterLocaleInitialValue(previousMasterLocale),
    )

  const [isLoading, setIsLoading] = useState(false)

  const onConfirmation = useCallback(
    async (selectedLocale: SelectedLocale) => {
      setIsLoading(true)

      try {
        // If master locale didn't changed, do nothing
        if (previousMasterLocale?.id === selectedLocale.id) {
          trackPbOnboardingStep(onboardingStepName)
          goToNextStep()
          return
        }

        await addRepositoryLocaleMutation.mutateAsync({
          id: selectedLocale.id,
          isMaster: true,
          customName: selectedLocale.customName,
        })

        // If there was a master locale before, we need to detach it
        if (previousMasterLocale) {
          await deleteRepositoryLocaleMutation.mutateAsync(previousMasterLocale)
        }

        void sharedOnboarding.completeStep("chooseLocale")
        trackPbOnboardingStep(onboardingStepName)
        goToNextStep()
      } catch {
        showAlert({
          title: "Error",
          subtitle: "Something went wrong when choosing the master locale.",
        })
      } finally {
        setIsLoading(false)
      }
    },
    [
      addRepositoryLocaleMutation,
      deleteRepositoryLocaleMutation,
      goToNextStep,
      previousMasterLocale,
      sharedOnboarding,
      showAlert,
    ],
  )

  return (
    <>
      <LocaleSelection
        locale={selectedMasterLocale}
        onLocaleChange={async (selectedLocale) => {
          setSelectedMasterLocale(selectedLocale)

          // If user added a new custom locale, complete step
          if (selectedLocale.customName) {
            await onConfirmation(selectedLocale)
          }
        }}
        locales={defaultLocales}
        addCustomLocaleEnabled
      />

      <Separator style="dashed" sx={{ marginBlock: 24 }} />

      <Button
        endIcon="arrowForward"
        size="large"
        onClick={() => onConfirmation(selectedMasterLocale)}
        loading={isLoading}
      >
        Define as the main language
      </Button>
    </>
  )
}

function getSelectedMasterLocaleInitialValue(
  previousMasterLocale: RepositoryLocale | undefined,
): SelectedLocale {
  if (previousMasterLocale) {
    return {
      id: previousMasterLocale.id,
      customName: previousMasterLocale.customName,
    }
  }

  return {
    id: defaultMasterLocaleId,
    customName: null,
  }
}

function ChooseMainLocaleContentSkeleton() {
  return (
    <>
      <Skeleton width="100%" height={40} />
      <Separator style="dashed" sx={{ marginBlock: 24 }} />
      <Button endIcon="arrowForward" size="large" loading>
        Define as the main language
      </Button>
    </>
  )
}
